/**
 * Organization Form Component
 *
 * Form for creating/editing organizations with authentication mode toggle.
 * Supports both create mode (new organization) and edit mode (existing organization).
 * Federated organizations require primary_domain and max_users.
 * Azure tenant ID is auto-discovered by the backend from the primary domain.
 */

import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Label,
  Radio,
  RadioGroup,
  Field,
  Spinner,
  Dropdown,
  Option,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { Alert } from '../common/Alert';
import { OrganizationApiService } from '../../services/organizationApi';
import type {
  AuthenticationMode,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationWithCounts,
  OrganizationStatus,
} from '../../types/organization';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  federationFields: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    marginTop: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginTop: tokens.spacingVerticalM,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

interface OrganizationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Organization to edit (if provided, form is in edit mode) */
  organization?: OrganizationWithCounts;
}

interface FormErrors {
  name?: string;
  primary_domain?: string;
  max_users?: string;
}

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  onSuccess,
  onCancel,
  organization,
}) => {
  const styles = useStyles();
  const isEditMode = !!organization;

  // Form state
  const [name, setName] = useState('');
  const [authenticationMode, setAuthenticationMode] = useState<AuthenticationMode>('home_tenant');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [maxUsers, setMaxUsers] = useState('');
  const [status, setStatus] = useState<OrganizationStatus>('active');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Populate form when editing
  useEffect(() => {
    if (organization) {
      setName(organization.name);
      setAuthenticationMode(organization.authentication_mode as AuthenticationMode);
      setPrimaryDomain(organization.primary_domain ?? '');
      setMaxUsers(organization.max_users?.toString() ?? '');
      setStatus(organization.status);
    } else {
      setName('');
      setAuthenticationMode('home_tenant');
      setPrimaryDomain('');
      setMaxUsers('');
      setStatus('active');
    }
    setSubmitError(null);
    setErrors({});
  }, [organization]);

  /**
   * Validate the form and return true if valid
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name is always required
    if (!name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    // Federated mode requires primary domain (tenant ID auto-discovered from domain)
    if (authenticationMode === 'federated') {
      if (!primaryDomain.trim()) {
        newErrors.primary_domain = 'Primary domain is required for federated organizations';
      }

      if (maxUsers.trim() && (isNaN(parseInt(maxUsers)) || parseInt(maxUsers) < 1)) {
        newErrors.max_users = 'User limit must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && organization) {
        // Update existing organization
        const updateData: OrganizationUpdate = {
          name: name.trim(),
          status,
        };

        // Add federation fields if federated mode
        if (authenticationMode === 'federated') {
          updateData.primary_domain = primaryDomain.trim();
          updateData.max_users = maxUsers.trim() ? parseInt(maxUsers) : undefined;
        }

        await OrganizationApiService.updateOrganization(organization.id, updateData);
      } else {
        // Create new organization
        const createData: OrganizationCreate = {
          name: name.trim(),
          authentication_mode: authenticationMode,
        };

        // Add federation fields only if federated mode
        // Note: azure_tenant_id is auto-discovered by backend from primary_domain
        if (authenticationMode === 'federated') {
          createData.primary_domain = primaryDomain.trim();
          if (maxUsers.trim()) {
            createData.max_users = parseInt(maxUsers);
          }
        }

        await OrganizationApiService.createOrganization(createData);
      }
      onSuccess?.();
    } catch (error) {
      const action = isEditMode ? 'update' : 'create';
      setSubmitError(error instanceof Error ? error.message : `Failed to ${action} organization`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFederated = authenticationMode === 'federated';

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {submitError && (
        <Alert intent="error">{submitError}</Alert>
      )}

      {/* Organization Name */}
      <div className={styles.fieldGroup}>
        <Field
          label="Name"
          required
          validationState={errors.name ? 'error' : 'none'}
        >
          <Input
            id="org-name"
            value={name}
            onChange={(_, data) => setName(data.value)}
            placeholder="Enter organization name"
            aria-label="Name"
          />
        </Field>
        {errors.name && (
          <span className={styles.errorText} role="alert">
            {errors.name}
          </span>
        )}
      </div>

      {/* Status - Only shown in edit mode */}
      {isEditMode && (
        <div className={styles.fieldGroup}>
          <Field label="Status">
            <Dropdown
              value={status === 'active' ? 'Active' : 'Suspended'}
              selectedOptions={[status]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  setStatus(data.optionValue as OrganizationStatus);
                }
              }}
            >
              <Option value="active">Active</Option>
              <Option value="suspended">Suspended</Option>
            </Dropdown>
          </Field>
        </div>
      )}

      {/* Authentication Mode - disabled in edit mode (can't change after creation) */}
      <div className={styles.fieldGroup}>
        <Label>Authentication Mode</Label>
        <RadioGroup
          value={authenticationMode}
          onChange={(_, data) => setAuthenticationMode(data.value as AuthenticationMode)}
          disabled={isEditMode}
        >
          <Radio value="home_tenant" label="Home Tenant" aria-label="Home Tenant" />
          <Radio value="federated" label="Federated" aria-label="Federated" />
        </RadioGroup>
      </div>

      {/* Federation Fields - Only shown when federated mode is selected */}
      {/* Note: Azure Tenant ID is auto-discovered by backend from primary domain */}
      {isFederated && (
        <div className={styles.federationFields}>
          <div className={styles.fieldGroup}>
            <Field
              label="Primary Domain"
              required
              validationState={errors.primary_domain ? 'error' : 'none'}
            >
              <Input
                id="primary-domain"
                value={primaryDomain}
                onChange={(_, data) => setPrimaryDomain(data.value)}
                placeholder="example.com"
                aria-label="Primary Domain"
              />
            </Field>
            {errors.primary_domain && (
              <span className={styles.errorText} role="alert">
                {errors.primary_domain}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <Field
              label="User Limit"
              validationState={errors.max_users ? 'error' : 'none'}
            >
              <Input
                id="max-users"
                type="number"
                value={maxUsers}
                onChange={(_, data) => setMaxUsers(data.value)}
                placeholder="Optional - leave empty for unlimited"
                aria-label="User Limit"
                min={1}
              />
            </Field>
            {errors.max_users && (
              <span className={styles.errorText} role="alert">
                {errors.max_users}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.buttonGroup}>
        {onCancel && (
          <Button appearance="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button appearance="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Spinner size="tiny" /> : isEditMode ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
};
